import {
  getCanvasItemBounds,
  getProjectionBlendZones,
  normalizeCanvasItemConfig,
  type ProjectionBlendZone,
} from './canvas';
import { getPatternMetrics } from './config';
import { drawPattern } from './renderer';
import type { CanvasDocument, CanvasItem } from './types';

type CanvasContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface CanvasRenderOptions {
  clear?: boolean;
  previewProjectionBlends?: boolean;
  showProjectionBlendZones?: boolean;
  projectionBlendScale?: number;
}

export interface ProjectionBlendPreviewLayer {
  item: CanvasItem;
  opacity: number;
}

export type CanvasTransform = [number, number, number, number, number, number];

export async function drawCanvasDocument(
  context: CanvasContext,
  canvas: CanvasDocument,
  options: CanvasRenderOptions = {},
): Promise<void> {
  const { width, height } = canvas.resolution;
  context.save();
  context.imageSmoothingEnabled = false;
  if (options.clear !== false) context.clearRect(0, 0, width, height);
  if (canvas.background === 'black') {
    context.fillStyle = '#000000';
    context.fillRect(0, 0, width, height);
  }

  for (const item of canvas.items) await drawCanvasItemPattern(context, item);
  if (options.previewProjectionBlends && canvas.canvasType === 'projection') {
    await drawProjectionBlendPreview(context, canvas);
  }
  for (const item of canvas.items) drawItemAnnotation(context, canvas, item);
  if (options.showProjectionBlendZones && canvas.canvasType === 'projection') {
    drawProjectionBlendZoneOverlay(
      context,
      getProjectionBlendZones(canvas),
      options.projectionBlendScale ?? 1,
    );
  }
  context.restore();
}

export function getProjectionBlendPreviewLayers(
  canvas: CanvasDocument,
  zone: ProjectionBlendZone,
): ProjectionBlendPreviewLayer[] {
  const zoneItems = canvas.items.filter((item) => zone.itemIds.includes(item.id));
  return zoneItems.slice(0, -1).map((item, index) => ({
    item,
    opacity: 1 / (index + 2),
  }));
}

export function getCanvasItemAnnotationLines(canvas: CanvasDocument, item: CanvasItem): string[] {
  const lines: string[] = [];
  if (canvas.annotations.showName) lines.push(item.name);
  if (canvas.annotations.showCoordinates) lines.push(`X ${item.x}  Y ${item.y}`);
  if (canvas.annotations.showResolution) {
    const { resolution } = getPatternMetrics(item.config);
    lines.push(`${resolution.width} × ${resolution.height} px`);
  }
  return lines;
}

export function getProjectionBlendExportScale(resolution: {
  width: number;
  height: number;
}): number {
  return Math.max(0.05, Math.min(1, 1600 / resolution.width, 900 / resolution.height));
}

export function drawProjectionBlendZoneOverlay(
  context: CanvasContext,
  blendZones: ProjectionBlendZone[],
  scale: number,
): void {
  for (const zone of blendZones) {
    context.save();
    context.setLineDash([]);
    context.fillStyle = 'rgba(255, 65, 190, 0.12)';
    context.fillRect(zone.x, zone.y, zone.width, zone.height);
    context.strokeStyle = '#ff70d2';
    context.lineWidth = Math.max(1, 2 / scale);
    context.strokeRect(zone.x, zone.y, zone.width, zone.height);

    if (zone.width * scale >= 96 && zone.height * scale >= 28) {
      const fontSize = 11 / scale;
      const paddingX = 6 / scale;
      const paddingY = 4 / scale;
      const label = `BLEND ×${zone.itemIds.length}  ${zone.width} × ${zone.height} px`;
      context.font = `700 ${fontSize}px "JetBrains Mono", monospace`;
      const labelWidth = Math.min(zone.width, context.measureText(label).width + paddingX * 2);
      const labelHeight = Math.min(zone.height, fontSize + paddingY * 2);
      context.fillStyle = 'rgba(38, 5, 29, 0.86)';
      context.fillRect(zone.x, zone.y, labelWidth, labelHeight);
      context.fillStyle = '#ffd8f3';
      context.textAlign = 'left';
      context.textBaseline = 'middle';
      context.fillText(
        label,
        zone.x + paddingX,
        zone.y + labelHeight / 2,
        Math.max(1, labelWidth - paddingX * 2),
      );
    }
    context.restore();
  }
}

export function drawCanvasItemProjectionBlendZones(
  context: CanvasContext,
  canvas: CanvasDocument,
  item: CanvasItem,
  scale: number,
): void {
  if (canvas.canvasType !== 'projection' || item.kind !== 'projection') return;
  const zones = getCanvasItemProjectionBlendZones(canvas, item);
  if (zones.length === 0) return;
  context.save();
  applyInverseItemTransform(context, item);
  drawProjectionBlendZoneOverlay(context, zones, scale);
  context.restore();
}

export function getCanvasItemProjectionBlendZones(
  canvas: CanvasDocument,
  item: CanvasItem,
): ProjectionBlendZone[] {
  if (canvas.canvasType !== 'projection' || item.kind !== 'projection') return [];
  return getProjectionBlendZones(canvas).filter((zone) => zone.itemIds.includes(item.id));
}

export function getCanvasItemInverseTransform(item: CanvasItem): CanvasTransform {
  const { resolution } = getPatternMetrics(item.config);
  const { width, height } = resolution;
  if (item.rotation === 0) return [1, 0, 0, 1, -item.x, -item.y];
  if (item.rotation === 90) return [0, -1, 1, 0, -item.y, item.x + height];
  if (item.rotation === 180) return [-1, 0, 0, -1, item.x + width, item.y + height];
  return [0, 1, -1, 0, item.y + width, -item.x];
}

async function drawProjectionBlendPreview(
  context: CanvasContext,
  canvas: CanvasDocument,
): Promise<void> {
  for (const zone of getProjectionBlendZones(canvas)) {
    const layers = getProjectionBlendPreviewLayers(canvas, zone);
    context.save();
    context.beginPath();
    context.rect(zone.x, zone.y, zone.width, zone.height);
    context.clip();
    for (const layer of layers) {
      await drawCanvasItemPattern(context, layer.item, layer.opacity);
    }
    context.restore();
  }
}

async function drawCanvasItemPattern(
  context: CanvasContext,
  item: CanvasItem,
  opacity = 1,
): Promise<void> {
  const { resolution } = getPatternMetrics(item.config);
  context.save();
  context.globalAlpha = opacity;
  applyItemTransform(context, item, resolution.width, resolution.height);
  context.beginPath();
  context.rect(0, 0, resolution.width, resolution.height);
  context.clip();
  await drawPattern(context, normalizeCanvasItemConfig(item.config), {
    clear: false,
    includePowerRoutes: false,
  });
  context.restore();
}

function applyItemTransform(
  context: CanvasContext,
  item: CanvasItem,
  width: number,
  height: number,
): void {
  if (item.rotation === 0) {
    context.translate(item.x, item.y);
  } else if (item.rotation === 90) {
    context.translate(item.x + height, item.y);
    context.rotate(Math.PI / 2);
  } else if (item.rotation === 180) {
    context.translate(item.x + width, item.y + height);
    context.rotate(Math.PI);
  } else {
    context.translate(item.x, item.y + width);
    context.rotate(-Math.PI / 2);
  }
}

function applyInverseItemTransform(context: CanvasContext, item: CanvasItem): void {
  context.transform(...getCanvasItemInverseTransform(item));
}

function drawItemAnnotation(
  context: CanvasContext,
  canvas: CanvasDocument,
  item: CanvasItem,
): void {
  const lines = getCanvasItemAnnotationLines(canvas, item);
  if (lines.length === 0) return;
  const bounds = getCanvasItemBounds(item);
  const shortSide = Math.min(bounds.width, bounds.height);
  let fontSize = clamp(Math.floor(shortSide * 0.045), 6, 54);
  const maxWidth = Math.max(1, bounds.width * 0.88);
  context.font = `700 ${fontSize}px Arial, sans-serif`;
  const measuredWidth = Math.max(...lines.map((line) => context.measureText(line).width));
  if (measuredWidth > maxWidth) {
    fontSize = Math.max(4, Math.floor(fontSize * (maxWidth / measuredWidth)));
  }

  const paddingX = Math.max(4, fontSize * 0.55);
  const paddingY = Math.max(3, fontSize * 0.38);
  const lineHeight = fontSize * 1.25;
  const boxWidth = Math.min(
    bounds.width,
    Math.max(...lines.map((line) => measureLine(context, line, fontSize))) + paddingX * 2,
  );
  const boxHeight = Math.min(bounds.height, lineHeight * lines.length + paddingY * 2);

  context.save();
  context.fillStyle = 'rgba(3, 8, 12, 0.88)';
  context.fillRect(bounds.x, bounds.y, boxWidth, boxHeight);
  context.strokeStyle = '#24c8db';
  context.lineWidth = Math.max(1, Math.round(fontSize * 0.08));
  context.strokeRect(bounds.x, bounds.y, boxWidth, boxHeight);
  context.font = `700 ${fontSize}px Arial, sans-serif`;
  context.textAlign = 'left';
  context.textBaseline = 'middle';
  context.fillStyle = '#9beef6';
  lines.forEach((line, index) => {
    context.fillText(
      line,
      bounds.x + paddingX,
      bounds.y + paddingY + lineHeight * (index + 0.5),
      Math.max(1, boxWidth - paddingX * 2),
    );
  });
  context.restore();
}

function measureLine(context: CanvasContext, line: string, fontSize: number): number {
  context.font = `700 ${fontSize}px Arial, sans-serif`;
  return context.measureText(line).width;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
